import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';

import RoadmapCard from './components/RoadmapCard';
import FilterSidebar from './components/FilterSidebar';
import ComparisonTool from './components/ComparisonTool';
import RecommendationEngine from './components/RecommendationEngine';
import Icon from '../../components/AppIcon';

const RoadmapSelectionHub = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [selectedRoadmaps, setSelectedRoadmaps] = useState([]);
  const [filters, setFilters] = useState({
    category: 'all',
    difficulty: 'all',
    duration: 'all',
    skillLevel: 'all'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showComparison, setShowComparison] = useState(false);

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Mock authenticated user data - updated to focus on testing
  const mockUser = {
    id: 1,
    username: "alex_developer",
    email: "alex@example.com",
    level: 3,
    progress: 65,
    selectedRoadmap: 'manual-testing', // User selected manual testing
    currentRoadmap: {
      id: 1,
      title: "Manual Testing Fundamentals", // Changed from "Full-Stack Web Development"
      progress: 45,
      currentModule: "Test Case Design Techniques", // Updated to testing module
      nextMilestone: "Bug Life Cycle Management", // Updated to testing milestone
      estimatedCompletion: "2025-03-15"
    }
  };

  // Simulate auth state - check authentication properly
  useEffect(() => {
    // Check for authentication token/session
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      setUser(mockUser);
    }
  }, []);

  // Enhanced roadmaps data with testing-focused roadmaps including user progress
  const roadmapsData = [
    {
      id: 1,
      title: "Manual Testing Fundamentals",
      description: "Master the art of manual testing with comprehensive test case design, execution strategies, and defect management.",
      category: "Testing",
      difficulty: "Beginner",
      duration: "6-8 weeks",
      skillLevel: "Beginner to Intermediate",
      technologies: ["Test Design", "Bug Reporting", "JIRA", "TestRail"],
      modules: 15,
      practiceProjects: 8,
      certifications: 2,
      prerequisites: ["Basic computer skills", "Understanding of software"],
      learningPath: [
        "Introduction to Software Testing",
        "Test Case Design Techniques",
        "Bug Life Cycle Management",
        "Test Execution Strategies",
        "Defect Reporting & Tracking",
        "Test Documentation",
        "Quality Assurance Fundamentals"
      ],
      features: ["Hands-on practice", "Real-world scenarios", "Industry best practices", "ISTQB preparation"],
      rating: 4.8,
      enrolledStudents: 1250,
      successRate: "92%",
      price: { original: 199, discounted: 149 },
      instructor: {
        name: "Sarah Testing Expert",
        expertise: "Senior QA Lead with 8+ years experience",
        rating: 4.9
      },
      isPopular: true,
      isEnrolled: isAuthenticated,
      progress: isAuthenticated ? (user?.selectedRoadmap === 'manual-testing' ? 65 : 0) : 0,
      nextModule: isAuthenticated && user?.selectedRoadmap === 'manual-testing' ? "Test Execution Strategies" : null
    },
    {
      id: 2,
      title: "Automation Testing with Java",
      description: "Build robust automation frameworks using Java, Selenium WebDriver, TestNG, and Maven for enterprise applications.",
      category: "Testing",
      difficulty: "Intermediate",
      duration: "12-14 weeks",
      skillLevel: "Intermediate to Advanced",
      technologies: ["Java", "Selenium WebDriver", "TestNG", "Maven"],
      modules: 25,
      practiceProjects: 12,
      certifications: 3,
      prerequisites: ["Java basics", "OOP concepts", "Manual testing experience"],
      learningPath: [
        "Java for Testers",
        "Selenium WebDriver Fundamentals",
        "TestNG Framework",
        "Page Object Model Pattern",
        "Data-Driven Testing",
        "Cross-Browser Testing",
        "CI/CD Integration"
      ],
      features: ["Live coding sessions", "Framework development", "Industry projects", "Jenkins integration"],
      rating: 4.7,
      enrolledStudents: 850,
      successRate: "78%",
      price: { original: 399, discounted: 299 },
      instructor: {
        name: "James Automation Expert",
        expertise: "Senior Test Automation Engineer at Tech Corp",
        rating: 4.8
      },
      isPopular: true,
      isEnrolled: isAuthenticated && user?.selectedRoadmap === 'automation-java',
      progress: isAuthenticated && user?.selectedRoadmap === 'automation-java' ? 45 : 0,
      nextModule: isAuthenticated && user?.selectedRoadmap === 'automation-java' ? "Page Object Model Pattern" : null
    },
    {
      id: 3,
      title: "Automation Testing with Python",
      description: "Learn Python-based automation testing with Selenium, pytest, and modern testing frameworks for rapid development.",
      category: "Testing",
      difficulty: "Intermediate",
      duration: "10-12 weeks",
      skillLevel: "Intermediate to Advanced",
      technologies: ["Python", "Selenium", "Pytest", "Requests"],
      modules: 22,
      practiceProjects: 10,
      certifications: 2,
      prerequisites: ["Python basics", "Basic programming", "Manual testing"],
      learningPath: [
        "Python for Test Automation",
        "Selenium with Python",
        "Pytest Framework Mastery",
        "API Testing with Requests",
        "Test Data Management",
        "Reporting and Logging",
        "Advanced Test Patterns"
      ],
      features: ["Pythonic test code", "API testing integration", "Modern frameworks", "Docker support"],
      rating: 4.9,
      enrolledStudents: 1100,
      successRate: "85%",
      price: { original: 349, discounted: 249 },
      instructor: {
        name: "Maria Python Expert",
        expertise: "Lead QA Engineer specializing in Python automation",
        rating: 4.9
      },
      isPopular: true,
      isNew: true,
      isEnrolled: false,
      progress: 0
    },
    {
      id: 4,
      title: "API Testing Mastery",
      description: "Comprehensive API testing with REST, GraphQL, Postman, RestAssured, and automated API validation.",
      category: "Testing",
      difficulty: "Intermediate",
      duration: "8-10 weeks",
      skillLevel: "Intermediate to Advanced",
      technologies: ["REST API", "Postman", "RestAssured", "JSON"],
      modules: 18,
      practiceProjects: 8,
      certifications: 2,
      prerequisites: ["HTTP/REST basics", "JSON understanding"],
      learningPath: [
        "API Testing Fundamentals",
        "REST API Deep Dive",
        "Postman for API Testing",
        "RestAssured Automation",
        "API Security Testing",
        "Performance Testing APIs",
        "GraphQL Testing"
      ],
      features: ["Real API testing", "Security focus", "Performance testing", "GraphQL coverage"],
      rating: 4.8,
      enrolledStudents: 920,
      successRate: "87%",
      price: { original: 299, discounted: 199 },
      instructor: {
        name: "Alex API Specialist",
        expertise: "Senior API Testing Consultant",
        rating: 4.8
      },
      isPopular: true,
      isEnrolled: false,
      progress: 0
    },
    {
      id: 5,
      title: "Performance Testing Excellence",
      description: "Master performance testing with JMeter, load testing strategies, and performance optimization techniques.",
      category: "Testing",
      difficulty: "Advanced",
      duration: "10-12 weeks",
      skillLevel: "Advanced to Expert",
      technologies: ["JMeter", "LoadRunner", "K6", "Grafana"],
      modules: 17,
      practiceProjects: 6,
      certifications: 2,
      prerequisites: ["Testing experience", "System architecture basics"],
      learningPath: [
        "Performance Testing Concepts",
        "JMeter Mastery",
        "Load Testing Strategies",
        "Performance Monitoring",
        "Bottleneck Analysis",
        "Scalability Testing",
        "Performance CI/CD"
      ],
      features: ["Real load testing", "Monitoring integration", "Enterprise scenarios", "Cloud testing"],
      rating: 4.7,
      enrolledStudents: 450,
      successRate: "75%",
      price: { original: 449, discounted: 349 },
      instructor: {
        name: "David Performance Expert",
        expertise: "Performance Testing Architect",
        rating: 4.7
      },
      isPopular: false,
      isEnrolled: false,
      progress: 0
    },
    {
      id: 6,
      title: "Mobile Testing with Appium",
      description: "Comprehensive mobile app testing for iOS and Android using Appium, device farms, and mobile-specific strategies.",
      category: "Testing",
      difficulty: "Advanced",
      duration: "12-14 weeks",
      skillLevel: "Advanced to Expert",
      technologies: ["Appium", "iOS", "Android", "Device Farms"],
      modules: 23,
      practiceProjects: 8,
      certifications: 2,
      prerequisites: ["Mobile development basics", "Automation experience"],
      learningPath: [
        "Mobile Testing Fundamentals",
        "Appium Setup & Configuration",
        "iOS App Testing",
        "Android App Testing",
        "Cross-Platform Testing",
        "Device Farm Integration",
        "Mobile Performance Testing"
      ],
      features: ["Real device testing", "Cloud device access", "Cross-platform focus", "Performance optimization"],
      rating: 4.4,
      enrolledStudents: 320,
      successRate: "68%",
      price: { original: 499, discounted: 399 },
      instructor: {
        name: "Lisa Mobile Expert",
        expertise: "Senior Mobile QA Engineer",
        rating: 4.6
      },
      isPopular: false,
      isEnrolled: false,
      progress: 0
    }
  ];

  // Filter roadmaps based on current filters and search
  const filteredRoadmaps = roadmapsData?.filter(roadmap => {
    const matchesSearch = roadmap?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         roadmap?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         roadmap?.technologies?.some(tech => tech?.toLowerCase()?.includes(searchQuery?.toLowerCase()));
    
    const matchesCategory = filters?.category === 'all' || roadmap?.category === filters?.category;
    const matchesDifficulty = filters?.difficulty === 'all' || roadmap?.difficulty === filters?.difficulty;
    const matchesDuration = filters?.duration === 'all' || roadmap?.duration?.includes(filters?.duration);
    const matchesSkillLevel = filters?.skillLevel === 'all' || roadmap?.skillLevel?.includes(filters?.skillLevel);

    return matchesSearch && matchesCategory && matchesDifficulty && matchesDuration && matchesSkillLevel;
  });

  const handleRoadmapSelect = (roadmapId) => {
    if (selectedRoadmaps?.includes(roadmapId)) {
      setSelectedRoadmaps(prev => prev?.filter(id => id !== roadmapId));
    } else if (selectedRoadmaps?.length < 3) {
      setSelectedRoadmaps(prev => [...prev, roadmapId]);
    }
  };

  const handleStartRoadmap = (roadmapId) => {
    if (isAuthenticated) {
      // In a real app, this would save the roadmap selection to the user's profile console.log('Starting roadmap:', roadmapId);
      navigate('/mission-map-dashboard');
    } else {
      navigate('/user-login');
    }
  };

  const handleLogout = () => {
    // Clear all authentication tokens and user data
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    
    // Clear user state
    setUser(null);
    
    // Navigate to home page
    navigate('/');
  };

  // Enhanced view for authenticated users
  if (isAuthenticated && user?.currentRoadmap) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} onLogout={handleLogout} />
        <main className="content-offset">
          <div className="max-w-7xl mx-auto px-4 py-8">
            {/* Current Roadmap Progress Section */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-6 mb-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-foreground mb-2">
                    Your Testing Journey
                  </h1>
                  <p className="text-muted-foreground">
                    Continue your progress on the {user?.currentRoadmap?.title} roadmap
                  </p>
                </div>
                <Button
                  variant="default"
                  iconName="Play"
                  iconPosition="left"
                  onClick={() => navigate('/mission-map-dashboard')}
                >
                  Continue Learning
                </Button>
              </div>

              {/* Current Roadmap Card */}
              <div className="bg-card rounded-lg border border-subtle p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-foreground mb-2">
                      {user?.currentRoadmap?.title}
                    </h2>
                    <p className="text-sm text-muted-foreground mb-3">
                      Currently studying: <span className="font-medium text-foreground">{user?.currentRoadmap?.currentModule}</span>
                    </p>
                    <p className="text-sm text-muted-foreground mb-4">
                      Next milestone: {user?.currentRoadmap?.nextMilestone}
                    </p>
                    
                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-foreground">
                          Overall Progress
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {user?.currentRoadmap?.progress}%
                        </span>
                      </div>
                      <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary transition-all duration-300"
                          style={{ width: `${user?.currentRoadmap?.progress}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={14} />
                        <span>Est. completion: {new Date(user?.currentRoadmap?.estimatedCompletion)?.toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Target" size={14} />
                        <span>{user?.currentRoadmap?.progress}% complete</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Suggested Roadmaps Section */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    Suggested Testing Roadmaps
                  </h2>
                  <p className="text-muted-foreground">
                    Based on your current progress and testing interests
                  </p>
                </div>
                <Button
                  variant="outline"
                  iconName="Grid"
                  iconPosition="left"
                  onClick={() => setShowComparison(!showComparison)}
                >
                  {showComparison ? 'Hide Comparison' : 'Compare Roadmaps'}
                </Button>
              </div>

              <RecommendationEngine 
                currentUser={user}
                roadmaps={roadmapsData}
                onSelectRoadmap={handleStartRoadmap}
              />
            </div>

            {/* All Testing Roadmaps */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-6">
                All Testing Learning Paths
              </h2>
              
              <div className="flex gap-8">
                <div className="w-64 flex-shrink-0">
                  <FilterSidebar
                    filters={filters}
                    onFilterChange={setFilters}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    selectedFilters={filters}
                    setSelectedFilters={setFilters}
                    onClose={() => {}}
                  />
                </div>

                <div className="flex-1">
                  {showComparison && selectedRoadmaps?.length > 0 && (
                    <div className="mb-8">
                      <ComparisonTool 
                        selectedRoadmaps={selectedRoadmaps}
                        roadmaps={roadmapsData}
                        onRemoveFromComparison={(id) => setSelectedRoadmaps(prev => prev?.filter(roadmapId => roadmapId !== id))}
                        onClose={() => setShowComparison(false)}
                        onClear={() => setSelectedRoadmaps([])}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredRoadmaps?.map((roadmap) => (
                      <RoadmapCard
                        key={roadmap?.id}
                        roadmap={roadmap}
                        isSelected={selectedRoadmaps?.includes(roadmap?.id)}
                        onSelect={handleRoadmapSelect}
                        onStartRoadmap={handleStartRoadmap}
                        onEnroll={handleStartRoadmap}
                        showComparison={showComparison}
                        isAuthenticated={isAuthenticated}
                      />
                    ))}
                  </div>

                  {filteredRoadmaps?.length === 0 && (
                    <div className="text-center py-12">
                      <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        No roadmaps found
                      </h3>
                      <p className="text-muted-foreground">
                        Try adjusting your filters or search terms
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Default view for non-authenticated users
  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogout={handleLogout} />
      <main className="content-offset">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Choose Your Testing Learning Path
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Discover comprehensive testing roadmaps designed by industry experts to help you master testing skills and advance your QA career.
            </p>
          </div>

          {/* Search and Filters */}
          <div className="flex gap-8 mb-8">
            <div className="w-64 flex-shrink-0">
              <FilterSidebar
                filters={filters}
                onFilterChange={setFilters}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                selectedFilters={filters}
                setSelectedFilters={setFilters}
                onClose={() => {}}
              />
            </div>

            <div className="flex-1">
              {/* Comparison Tool */}
              {showComparison && selectedRoadmaps?.length > 0 && (
                <div className="mb-8">
                  <ComparisonTool 
                    selectedRoadmaps={selectedRoadmaps}
                    roadmaps={roadmapsData}
                    onRemoveFromComparison={(id) => setSelectedRoadmaps(prev => prev?.filter(roadmapId => roadmapId !== id))}
                    onClose={() => setShowComparison(false)}
                    onClear={() => setSelectedRoadmaps([])}
                  />
                </div>
              )}

              {/* Action Bar */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <p className="text-sm text-muted-foreground">
                    {filteredRoadmaps?.length} testing roadmaps available
                  </p>
                  {selectedRoadmaps?.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowComparison(!showComparison)}
                    >
                      {showComparison ? 'Hide' : 'Show'} Comparison ({selectedRoadmaps?.length})
                    </Button>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Grid"
                    onClick={() => {}}
                  >
                    Grid View
                  </Button>
                </div>
              </div>

              {/* Roadmaps Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredRoadmaps?.map((roadmap) => (
                  <RoadmapCard
                    key={roadmap?.id}
                    roadmap={roadmap}
                    isSelected={selectedRoadmaps?.includes(roadmap?.id)}
                    onSelect={handleRoadmapSelect}
                    onStartRoadmap={handleStartRoadmap}
                    onEnroll={handleStartRoadmap}
                    showComparison={showComparison}
                    isAuthenticated={isAuthenticated}
                  />
                ))}
              </div>

              {filteredRoadmaps?.length === 0 && (
                <div className="text-center py-12">
                  <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    No roadmaps found
                  </h3>
                  <p className="text-muted-foreground">
                    Try adjusting your filters or search terms
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RoadmapSelectionHub;